import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, MessageCircle, Instagram, Shield, ChevronUp } from 'lucide-react';
import { contactData } from '../data/mock';
import { useConsent } from '../context/ConsentContext';
import { usePractice } from '../context/PracticeContext';
import { formatAddress } from '../data/practice';
import Logo from './Logo';
import useSitePages from '../hooks/useSitePages';
import { legals, legalMenuOrder, legalsBySlug } from '../data/legals';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { reopenBanner } = useConsent();
  const practice = usePractice();
  const sitePages = useSitePages();

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}?text=Hello! I'd like to book a consultation`, '_blank');
  };

  const handleNavClick = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-grid">
          <div className="footer-col">
            <div className="footer-logo-container">
              {/* White-on-transparent logo matches the dark navy footer
                  background — the color JPG shows a jarring white rectangle. */}
              <Logo variant="white" />
            </div>
            <p className="footer-description">
              Specialist physiotherapy for TMD, headaches, migraines & tinnitus.
              Evidence-led assessment and treatment, now in Waterloo.
            </p>
            <div className="footer-social">
              <a
                href="https://www.instagram.com/krinapanchal/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Follow Krina on Instagram (opens in new tab)"
                data-testid="footer-instagram-link"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          <div className="footer-col">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><button onClick={() => handleNavClick('#home')}>Home</button></li>
              <li><button onClick={() => handleNavClick('#services')}>Services</button></li>
              <li><button onClick={() => handleNavClick('#about')}>About</button></li>
              <li><button onClick={() => handleNavClick('#testimonials')}>Reviews</button></li>
              <li><button onClick={() => handleNavClick('#faq')}>FAQ</button></li>
              <li><button onClick={() => handleNavClick('#contact')}>Contact</button></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4 className="footer-heading">Services</h4>
            <ul className="footer-links">
              <li><a href="#services">TMD Treatment</a></li>
              <li><a href="#services">Headache Relief</a></li>
              <li><a href="#services">Migraine Management</a></li>
              <li><a href="#services">Tinnitus Treatment</a></li>
              <li><a href="#services">Facial Pain Therapy</a></li>
              <li><a href="/fees" data-testid="footer-link-fees">Fees &amp; Pricing</a></li>
              <li><Link to="/what-to-expect" data-testid="footer-link-what-to-expect">What to Expect</Link></li>
              {sitePages.filter((p) => p.in_footer).map((p) => (
                <li key={p.slug}><Link to={`/${p.slug}`} data-testid={`footer-link-page-${p.slug}`}>{p.nav_label}</Link></li>
              ))}
              <li><Link to="/the-tmd-physio-standard" data-testid="footer-link-standard">The TMD Physio Standard</Link></li>
              <li><Link to="/technology-and-treatments" data-testid="footer-link-technology">Technology &amp; Treatments</Link></li>
              <li><Link to="/evidence-library" data-testid="footer-link-evidence">Evidence Library</Link></li>
              <li><Link to="/areas-we-serve" data-testid="footer-link-areas">Areas We Serve</Link></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4 className="footer-heading">Contact</h4>
            <ul className="footer-contact">
              <li>
                <MapPin size={18} />
                <span data-testid="footer-address">
                  The TMD Physio<br />
                  {formatAddress(practice)}
                </span>
              </li>
              <li>
                <Phone size={18} />
                <a href={`tel:${contactData.phone}`}>{contactData.phone}</a>
              </li>
              <li>
                <Mail size={18} />
                <a href={`mailto:${contactData.email}`}>{contactData.email}</a>
              </li>
              <li>
                <MessageCircle size={18} />
                <button onClick={handleWhatsApp}>WhatsApp Us</button>
              </li>
              <li className="footer-contact-legal-item">
                <Shield size={18} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="footer-contact-legal-btn"
                      data-testid="footer-legal-trigger"
                      aria-label="Open Legal & Policies menu"
                    >
                      Legal &amp; Policies
                      <ChevronUp size={12} aria-hidden="true" className="footer-contact-legal-chev" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    side="top"
                    sideOffset={8}
                    className="footer-legal-menu"
                  >
                    <DropdownMenuLabel>Policies &amp; Regulatory</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {legalMenuOrder.map((slug) => {
                      const doc = legalsBySlug[slug];
                      if (!doc) return null;
                      return (
                        <DropdownMenuItem key={slug} asChild>
                          <Link
                            to={`/legal/${slug}`}
                            data-testid={`footer-legal-link-${slug}`}
                          >
                            {doc.title}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        reopenBanner();
                      }}
                      data-testid="footer-cookie-preferences"
                    >
                      Cookie preferences
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="footer-copyright">
              © {currentYear} The TMD Physio. All rights reserved.
            </p>
            <Link
              to="/admin/login"
              className="footer-admin-link"
              data-testid="footer-admin-link"
            >
              <Shield size={13} aria-hidden="true" /> Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Re-export for tests / external consumers that want the list without
// pulling the whole Footer.
export const footerLegalCount = legals.length;

export default Footer;
